import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from 'framer-motion';

import { BrandLogo } from '@/components/BrandLogo';
import { ChatIcon, CheckIcon } from '@/components/icons';
import { openAppStore } from '@/constants/links';
import type { Palette } from '@/constants/theme';

import styles from './LandingScreen.module.css';

/* ---------------------------------- data --------------------------------- */

const WAVE_BARS = Array.from({ length: 140 }, (_, i) => {
  const h = 0.22 + 0.72 * Math.abs(Math.sin(i * 1.7) * Math.cos(i * 0.53));
  return { h: Math.round(h * 100), d: (i % 9) * 0.12 };
});

const MARQUEE_ITEMS = [
  'STANDUPS',
  'CLIENT CALLS',
  'LECTURES',
  'JOURNALS',
  'BRAINSTORMS',
  'WIDGET TAP',
  'SIRI CAPTURE',
  'FIELD NOTES',
  'DAILY RECAPS',
];

const FORMAT_CHIPS = [
  'Summary',
  'To-do list',
  'Message',
  'Email',
  'Meeting notes',
  'Invoice',
  'Lecture notes',
  'Journal',
  'Brainstorm',
];

const DEMO_TABS = ['Meeting notes', 'To-do list', 'Email', 'Lecture notes'] as const;

const FEATURES = [
  {
    title: 'Nine note formats',
    sub: 'Summary, to-do, message, email, meeting, invoice, lecture, journal, and brainstorm — create any format from a note whenever you need it.',
    icon: <LayersIcon />,
    wide: true,
  },
  {
    title: 'Custom formats',
    sub: 'Pro users build their own output styles — tweet threads, SOAP notes, blog outlines, or anything you can describe in a prompt.',
    icon: <WandIcon />,
  },
  {
    title: 'Ask your notes',
    sub: '"What did we promise the client in March?" Chat across every note you have ever recorded and get an answer.',
    icon: <ChatIcon size={22} />,
  },
  {
    title: 'Translate anything',
    sub: 'Turn any note into 12+ languages without leaving the app. Speak in one language, send in another.',
    icon: <GlobeIcon />,
  },
  {
    title: 'Import & transcribe',
    sub: 'Drop in existing voice memos to transcribe and sort. Live on-device transcription while you record.',
    icon: <ImportIcon />,
  },
  {
    title: 'Tags, search & favourites',
    sub: 'Full-text search across transcripts and structured output. Star notes and auto-tag with keyword rules.',
    icon: <SearchIcon />,
  },
  {
    title: 'Cloud backup',
    sub: 'Notes, audio, and attachments sync automatically to your account — restored on any device.',
    icon: <CloudIcon />,
  },
  {
    title: 'PDF, share & reminders',
    sub: 'Export polished PDFs, share to WhatsApp or email, and turn "remind me…" phrases into scheduled nudges.',
    icon: <FileIcon />,
  },
];

const PLATFORM_SURFACES = [
  {
    title: 'Home Screen Widget',
    sub: 'Tap to record from your home screen. See your last note at a glance.',
    icon: <WidgetIcon />,
  },
  {
    title: 'Apple Watch',
    sub: 'Capture on your wrist. Notes sync to your phone automatically.',
    icon: <WatchIcon />,
  },
  {
    title: 'Live Activity',
    sub: 'Recording timer on your Lock Screen and Dynamic Island.',
    icon: <LiveActivityIcon />,
  },
  {
    title: 'Siri & Shortcuts',
    sub: '"Take a note in Voice Notes" — hands-free capture from anywhere.',
    icon: <SiriIcon />,
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Hit record',
    sub: 'Talk through the meeting, the idea, the plan — up to 90 minutes per note on Pro, straight from your phone.',
  },
  {
    num: '02',
    title: 'AI writes it up',
    sub: 'Live transcription while you talk — with speaker labels when multiple voices are detected. Then create the formats you need from the note.',
  },
  {
    num: '03',
    title: 'Send it anywhere',
    sub: 'Copy it, export a PDF, share it with the team, or set a reminder on the spot.',
  },
];

const FREE_ITEMS = [
  '5-minute recordings',
  '10 notes on the clipboard',
  'Summary & to-do formats',
  'iOS & Android',
];

const PRO_ITEMS = [
  '90-minute recordings',
  'Unlimited notes',
  'All note formats',
  'Custom formats',
  'Ask-your-notes AI chat',
  'Translate to 12+ languages',
  'Automatic cloud backup',
  'PDF export & attachments',
  'No ads',
];

/* -------------------------------- helpers -------------------------------- */

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px 0px' }}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}>
      {children}
    </motion.div>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className={styles.eyebrow}>
      <span className={styles.eyebrowSquare} aria-hidden />
      {children}
    </p>
  );
}

function fmtClock(s: number) {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/* --------------------------------- screen -------------------------------- */

type LandingScreenProps = { colors: Palette };

export function LandingScreen({ colors }: LandingScreenProps) {
  const reduceMotion = useReducedMotion();
  const start = () => openAppStore();

  const [tab, setTab] = useState(0);
  const [tabPaused, setTabPaused] = useState(false);
  const [clock, setClock] = useState(47);

  useEffect(() => {
    if (reduceMotion || tabPaused) return;
    const id = setInterval(() => setTab((t) => (t + 1) % DEMO_TABS.length), 4200);
    return () => clearInterval(id);
  }, [reduceMotion, tabPaused]);

  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => setClock((c) => c + 1), 1000);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const lineIn = (delay: number) =>
    ({
      initial: { y: '110%' },
      animate: { y: '0%' },
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const, delay },
    }) as const;

  return (
    <MotionConfig reducedMotion="user">
      <div className={styles.root}>
        <a href="#main" className={styles.skipLink}>
          Skip to content
        </a>

        {/* ------------------------------ header ------------------------------ */}
        <header className={styles.header}>
          <div className={styles.headerInner}>
            <div className={styles.brand}>
              <BrandLogo className={styles.brandMark} />
              <span className={styles.brandName}>Voice Notes</span>
              <span className={styles.brandAi}>AI</span>
            </div>
            <nav className={styles.nav} aria-label="Landing sections">
              <a className={styles.navLink} href="#demo">
                Demo
              </a>
              <a className={styles.navLink} href="#formats">
                Formats
              </a>
              <a className={styles.navLink} href="#features">
                Features
              </a>
              <a className={styles.navLink} href="#platform">
                Platform
              </a>
              <a className={styles.navLink} href="#pricing">
                Pricing
              </a>
            </nav>
            <div className={styles.headerActions}>
              <button type="button" className={styles.headerCta} onClick={start}>
                Download app
              </button>
            </div>
          </div>
        </header>

        <main id="main">
          {/* ------------------------------- hero ------------------------------- */}
          <section className={styles.hero}>
            <div className={styles.heroInner}>
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}>
                <Eyebrow>AI meetings &amp; notes</Eyebrow>
              </motion.div>

              <h1 className={styles.h1}>
                <span className={styles.line}>
                  <motion.span className={styles.lineInner} {...lineIn(0.08)}>
                    Your meetings,
                  </motion.span>
                </span>
                <span className={styles.line}>
                  <motion.span className={styles.lineInner} {...lineIn(0.2)}>
                    already{' '}
                    <span className={styles.accentWrap}>
                      written.
                      <motion.svg
                        className={styles.chalk}
                        viewBox="0 0 100 4"
                        preserveAspectRatio="none"
                        aria-hidden
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.5, delay: 0.85, ease: 'easeOut' }}>
                        <polygon
                          points="0,1.8 12,0.2 34,2.4 55,0.48 78,2.6 100,0.8 100,4 0,4"
                          fill={colors.hivis}
                        />
                      </motion.svg>
                    </span>
                  </motion.span>
                </span>
              </h1>

              <motion.p
                className={styles.sub}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.45, ease: 'easeOut' }}>
                Record meetings, lectures, and ideas. Voice Notes turns them into clean
                summaries, action items, and ready-to-send emails — create any format from a
                note, on iPhone, Apple Watch, and Android.
              </motion.p>

              <motion.div
                className={styles.ctaRow}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.58, ease: 'easeOut' }}>
                <button type="button" className={styles.btnPrimary} onClick={start}>
                  Download free
                  <ArrowRightIcon />
                </button>
                <a className={styles.btnGhost} href="#demo">
                  See it work
                </a>
              </motion.div>

              <motion.ul
                className={styles.trustRow}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.75 }}>
                <li>Free to start</li>
                <li>No card needed</li>
                <li>iOS &amp; Android</li>
              </motion.ul>
            </div>

            <div className={styles.waveStrip} aria-hidden>
              <span className={styles.recChip}>
                <span className={styles.recDot} />
                REC {fmtClock(clock)}
              </span>
              <div className={styles.wave}>
                {WAVE_BARS.map((b, i) => (
                  <span
                    key={i}
                    className={styles.waveBar}
                    style={{ height: `${b.h}%`, animationDelay: `${b.d}s` } as CSSProperties}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* ------------------------------ marquee ----------------------------- */}
          <div className={styles.marquee} aria-hidden>
            <div className={styles.marqueeTrack}>
              {[0, 1].map((g) => (
                <div className={styles.marqueeGroup} key={g}>
                  {MARQUEE_ITEMS.map((item) => (
                    <span className={styles.marqueeItem} key={item}>
                      {item}
                      <span className={styles.marqueeSquare} />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* --------------------------- formats --------------------------- */}
          <section className={styles.sectionAlt} id="formats">
            <div className={styles.section}>
              <Reveal className={styles.sectionHead}>
                <Eyebrow>9 formats</Eyebrow>
                <h2 className={styles.h2}>One voice memo. Nine ways to shape it.</h2>
              </Reveal>

              <div className={styles.templateChips} role="list" aria-label="Note formats">
                {FORMAT_CHIPS.map((name, i) => (
                  <motion.span
                    key={name}
                    role="listitem"
                    className={styles.templateChip}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px 0px' }}
                    transition={{ duration: 0.4, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={reduceMotion ? undefined : { y: -3 }}>
                    {name}
                  </motion.span>
                ))}
              </div>

              <Reveal delay={0.2}>
                <p className={styles.templateNote}>
                  Create any format from a note — plus custom formats you define yourself on Pro.
                </p>
              </Reveal>
            </div>
          </section>

          {/* ------------------------------- demo ------------------------------- */}
          <section className={styles.section} id="demo">
            <Reveal className={styles.sectionHead}>
              <Eyebrow>The transformation</Eyebrow>
              <h2 className={styles.h2}>From rambling to ready-to-send.</h2>
            </Reveal>

            <div className={styles.demoGrid}>
              <Reveal>
                <article className={`${styles.demoCard} ${styles.demoCardIn}`}>
                  <p className={styles.demoLabel}>
                    <span className={styles.recDot} />
                    You say
                  </p>
                  <p className={styles.transcript}>
                    &ldquo;okay so — quick recap of the design review… Priya will send the{' '}
                    <mark className={styles.mark}>final mocks by Thursday</mark>, we agreed to move
                    the <mark className={styles.mark}>launch to the 14th</mark>, and someone needs
                    to <mark className={styles.mark}>loop in finance</mark> about the new pricing
                    before that goes out…&rdquo;
                  </p>
                  <div className={styles.miniWave} aria-hidden>
                    {WAVE_BARS.slice(0, 42).map((b, i) => (
                      <span
                        key={i}
                        className={styles.waveBar}
                        style={{ height: `${b.h}%`, animationDelay: `${b.d}s` } as CSSProperties}
                      />
                    ))}
                  </div>
                </article>
              </Reveal>

              <motion.div
                className={styles.demoArrow}
                aria-hidden
                animate={reduceMotion ? undefined : { x: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}>
                <ArrowRightIcon size={30} />
              </motion.div>

              <Reveal delay={0.12}>
                <div className={styles.tabs} role="group" aria-label="Example output format">
                  {DEMO_TABS.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      className={styles.tabBtn}
                      aria-pressed={tab === i}
                      onClick={() => {
                        setTab(i);
                        setTabPaused(true);
                      }}>
                      {label}
                    </button>
                  ))}
                </div>

                <article className={`${styles.demoCard} ${styles.demoCardOut}`}>
                  <p className={styles.demoLabel}>
                    <SparkIcon />
                    You get
                  </p>
                  <div className={styles.noteStage}>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={tab}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.28, ease: 'easeOut' }}>
                        {tab === 0 && <DemoMeeting />}
                        {tab === 1 && <DemoTodo />}
                        {tab === 2 && <DemoEmail />}
                        {tab === 3 && <DemoLecture />}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  <div className={styles.chipRow} aria-hidden>
                    <span className={styles.chip}>Copy</span>
                    <span className={styles.chip}>PDF</span>
                    <span className={styles.chip}>Share</span>
                  </div>
                </article>
              </Reveal>
            </div>
          </section>

          {/* ----------------------------- features ----------------------------- */}
          <section className={styles.sectionAlt} id="features">
            <div className={styles.section}>
              <Reveal className={styles.sectionHead}>
                <Eyebrow>What you get</Eyebrow>
                <h2 className={styles.h2}>Built for people who think out loud.</h2>
              </Reveal>

              <div className={styles.featGrid}>
                {FEATURES.map((f, i) => (
                  <Reveal key={f.title} delay={(i % 3) * 0.08}>
                    <motion.article
                      className={`${styles.featCard} ${f.wide ? styles.featCardWide : ''}`}
                      whileHover={reduceMotion ? undefined : { y: -4 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}>
                      <div className={styles.featTop}>
                        <span className={styles.featIcon}>{f.icon}</span>
                        <span className={styles.featIndex}>{String(i + 1).padStart(2, '0')}</span>
                      </div>
                      <h3 className={styles.featTitle}>{f.title}</h3>
                      <p className={styles.featSub}>{f.sub}</p>
                    </motion.article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ----------------------------- platform ----------------------------- */}
          <section className={styles.section} id="platform">
            <Reveal className={styles.sectionHead}>
              <Eyebrow>Capture anywhere</Eyebrow>
              <h2 className={styles.h2}>Record without opening the app.</h2>
            </Reveal>

            <div className={styles.platformGrid}>
              {PLATFORM_SURFACES.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.1}>
                  <motion.article
                    className={styles.platformCard}
                    whileHover={reduceMotion ? undefined : { y: -4, scale: 1.01 }}
                    whileTap={reduceMotion ? undefined : { scale: 0.99 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}>
                    <span className={styles.platformIcon}>{p.icon}</span>
                    <h3 className={styles.platformTitle}>{p.title}</h3>
                    <p className={styles.platformSub}>{p.sub}</p>
                  </motion.article>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --------------------------- how it works --------------------------- */}
          <section className={styles.section} id="how">
            <Reveal className={styles.sectionHead}>
              <Eyebrow>How it works</Eyebrow>
              <h2 className={styles.h2}>Three steps. Zero typing.</h2>
            </Reveal>

            <div className={styles.howGrid}>
              {STEPS.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.12}>
                  <div className={styles.howStep}>
                    <span className={styles.howNum} aria-hidden>
                      {s.num}
                    </span>
                    <h3 className={styles.howTitle}>{s.title}</h3>
                    <p className={styles.howSub}>{s.sub}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* ------------------------------ pricing ----------------------------- */}
          <section className={styles.sectionAlt} id="pricing">
            <div className={styles.section}>
              <Reveal className={styles.sectionHead}>
                <Eyebrow>Pricing</Eyebrow>
                <h2 className={styles.h2}>Start free. Upgrade when it sticks.</h2>
              </Reveal>

              <div className={styles.priceGrid}>
                <Reveal>
                  <article className={styles.priceCard}>
                    <p className={styles.priceName}>Free</p>
                    <p className={styles.priceAmt}>
                      $0
                      <span className={styles.pricePer}> / forever</span>
                    </p>
                    <p className={styles.priceNote}>No card needed</p>
                    <ul className={styles.priceList}>
                      {FREE_ITEMS.map((item) => (
                        <li key={item} className={styles.priceItem}>
                          <CheckIcon size={16} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={`${styles.btnGhost} ${styles.priceCta}`}
                      onClick={start}>
                      Download app
                    </button>
                  </article>
                </Reveal>

                <Reveal delay={0.12}>
                  <article className={`${styles.priceCard} ${styles.priceCardPro}`}>
                    <p className={styles.priceBadge}>7-day free trial</p>
                    <p className={styles.priceName}>Pro</p>
                    <p className={styles.priceAmt}>
                      $4.99
                      <span className={styles.pricePer}> / month</span>
                    </p>
                    <p className={styles.priceNote}>or $29.99 / year · cancel anytime</p>
                    <ul className={styles.priceList}>
                      {PRO_ITEMS.map((item) => (
                        <li key={item} className={styles.priceItem}>
                          <CheckIcon size={16} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={`${styles.btnPrimary} ${styles.priceCta}`}
                      onClick={start}>
                      Download &amp; try free
                    </button>
                  </article>
                </Reveal>
              </div>
            </div>
          </section>

          {/* ------------------------------ CTA band ---------------------------- */}
          <section className={styles.ctaBand}>
            <Reveal className={styles.ctaBandInner}>
              <h2 className={styles.ctaBandTitle}>Stop typing. Start talking.</h2>
              <p className={styles.ctaBandSub}>Your next meeting can write itself.</p>
              <button type="button" className={styles.ctaBandBtn} onClick={start}>
                Download — it&rsquo;s free
                <ArrowRightIcon />
              </button>
            </Reveal>
          </section>
        </main>

        {/* ------------------------------- footer ------------------------------ */}
        <footer className={styles.footer}>
          <div className={styles.footerInner}>
            <div className={styles.brand}>
              <BrandLogo className={styles.brandMark} />
              <span className={styles.brandName}>Voice Notes</span>
              <span className={styles.brandAi}>AI</span>
            </div>
            <nav className={styles.footerLinks} aria-label="Footer">
              <a className={styles.footerLink} href="#formats">
                Formats
              </a>
              <a className={styles.footerLink} href="#features">
                Features
              </a>
              <a className={styles.footerLink} href="#platform">
                Platform
              </a>
              <a className={styles.footerLink} href="#how">
                How it works
              </a>
              <a className={styles.footerLink} href="#pricing">
                Pricing
              </a>
            </nav>
            <p className={styles.footerCopy}>© 2026 Voice Notes</p>
          </div>
        </footer>
      </div>
    </MotionConfig>
  );
}

/* ------------------------------ demo content ----------------------------- */

function DemoMeeting() {
  return (
    <div>
      <h3 className={styles.noteTitle}>Design review — recap</h3>
      <p className={styles.noteBlockLabel}>Attendees</p>
      <p className={styles.noteText}>Priya · Sam · You</p>
      <p className={styles.noteBlockLabel}>Notes</p>
      <ul className={styles.noteList}>
        <li className={styles.noteBullet}>Final mocks locked for handoff</li>
        <li className={styles.noteBullet}>Launch moved to the 14th</li>
      </ul>
      <p className={styles.noteBlockLabel}>Action items</p>
      <ul className={styles.noteList}>
        <li className={styles.actionRow}>
          <span className={`${styles.checkbox} ${styles.checkboxDone}`} aria-hidden>
            <CheckIcon size={12} />
          </span>
          Priya — send final mocks by Thu
        </li>
        <li className={styles.actionRow}>
          <span className={styles.checkbox} aria-hidden />
          You — loop in finance on pricing
        </li>
      </ul>
    </div>
  );
}

function DemoTodo() {
  return (
    <div>
      <h3 className={styles.noteTitle}>After the design review</h3>
      <ul className={styles.noteList}>
        <li className={styles.actionRow}>
          <span className={`${styles.checkbox} ${styles.checkboxDone}`} aria-hidden>
            <CheckIcon size={12} />
          </span>
          Send final mocks
          <span className={styles.dueChip}>Thu · High</span>
        </li>
        <li className={styles.actionRow}>
          <span className={styles.checkbox} aria-hidden />
          Loop in finance on pricing
          <span className={styles.dueChip}>This week</span>
        </li>
        <li className={styles.actionRow}>
          <span className={styles.checkbox} aria-hidden />
          Book launch retro
          <span className={styles.dueChip}>Fri</span>
        </li>
      </ul>
    </div>
  );
}

function DemoEmail() {
  return (
    <div>
      <h3 className={styles.noteTitle}>Design review recap + next steps</h3>
      <p className={styles.noteBlockLabel}>Subject</p>
      <p className={styles.noteText}>Design review recap — launch moves to the 14th</p>
      <p className={styles.noteBlockLabel}>Body</p>
      <p className={styles.noteText}>
        Hi team — quick recap from today: mocks are locked and launch moves to the 14th. Priya
        sends finals by Thursday; I&rsquo;ll loop in finance on the pricing change.
      </p>
    </div>
  );
}

function DemoLecture() {
  return (
    <div>
      <h3 className={styles.noteTitle}>Intro to product design — lecture</h3>
      <p className={styles.noteBlockLabel}>Topics covered</p>
      <ul className={styles.noteList}>
        <li className={styles.noteBullet}>User research methods</li>
        <li className={styles.noteBullet}>Wireframing vs prototyping</li>
        <li className={styles.noteBullet}>Design systems at scale</li>
      </ul>
      <p className={styles.noteBlockLabel}>Key terms</p>
      <p className={styles.noteText}>Affordance · Heuristic evaluation · Design token</p>
      <p className={styles.noteBlockLabel}>Review questions</p>
      <ul className={styles.noteList}>
        <li className={styles.noteBullet}>What makes a good design system?</li>
        <li className={styles.noteBullet}>When to use qualitative vs quantitative research?</li>
      </ul>
    </div>
  );
}

/* --------------------------------- icons --------------------------------- */

type IconProps = { size?: number };

function ArrowRightIcon({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 12h17M13 4.5L20.5 12 13 19.5" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

function SparkIcon({ size = 15 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2.5l2.2 6.3 6.3 2.2-6.3 2.2L12 19.5l-2.2-6.3-6.3-2.2 6.3-2.2z"
        fill="currentColor"
      />
    </svg>
  );
}

function LayersIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3l9 5-9 5-9-5z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M3 13l9 5 9-5" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M3 17l9 5 9-5" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
    </svg>
  );
}

function GlobeIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
      <path d="M3 12h18M12 3c2.5 2.4 3.8 5.6 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.6-3.8-9S9.5 5.4 12 3z" stroke="currentColor" strokeWidth="2.2" />
    </svg>
  );
}

function FileIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 2.5h8l4 4V21.5H6z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M14 2.5v4h4M9 12h6M9 16h6" stroke="currentColor" strokeWidth="2.2" />
    </svg>
  );
}

function WandIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 20l6-6M14 4l6 6M9 9l6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M15 3l2 2M17 5l2 2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function ImportIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3v10m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2.5" />
      <path d="M15.5 15.5L21 21" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

function CloudIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M7 18h11a4 4 0 0 0 .5-8 5.5 5.5 0 0 0-10.6-1.4A3.5 3.5 0 0 0 7 18Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WidgetIcon({ size = 24 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="8" height="8" stroke="currentColor" strokeWidth="2.2" />
      <rect x="13" y="3" width="8" height="5" stroke="currentColor" strokeWidth="2.2" />
      <rect x="13" y="10" width="8" height="11" stroke="currentColor" strokeWidth="2.2" />
      <rect x="3" y="13" width="8" height="8" stroke="currentColor" strokeWidth="2.2" />
    </svg>
  );
}

function WatchIcon({ size = 24 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="6" y="4" width="12" height="16" rx="3" stroke="currentColor" strokeWidth="2.2" />
      <path d="M9 4V2M15 4V2M9 20v2M15 20v2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" />
    </svg>
  );
}

function LiveActivityIcon({ size = 24 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="6" width="20" height="12" rx="6" stroke="currentColor" strokeWidth="2.2" />
      <circle cx="8" cy="12" r="2" fill="currentColor" />
      <path d="M12 10.5h6M12 13.5h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SiriIcon({ size = 24 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
      <path d="M8 14c.8 1.5 2.2 2.5 4 2.5s3.2-1 4-2.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="9" cy="10" r="1.2" fill="currentColor" />
      <circle cx="15" cy="10" r="1.2" fill="currentColor" />
    </svg>
  );
}
